import { z } from 'zod';

import {
  type AudienceChannel,
  type ByteUnit,
  type CampaignFilterField,
  type CampaignFilterInput,
  type CampaignFilterOperator,
  type CampaignFilterRule,
  type CampaignFilterService,
  campaignFilterDefinition,
  campaignFilterFields,
} from './contracts';

export interface CampaignFilterFormRule {
  field: CampaignFilterField | '';
  operator: CampaignFilterOperator | '';
  service?: CampaignFilterService;
  amount?: number;
  unit?: ByteUnit;
  booleanValue?: boolean;
  values: string[];
}

export interface CampaignFilterForm {
  rules: CampaignFilterFormRule[];
}

export const emptyCampaignFilter: CampaignFilterForm = { rules: [] };

const byteMultipliers: Record<ByteUnit, number> = {
  B: 1,
  KB: 1024,
  MB: 1024 * 1024,
  GB: 1024 * 1024 * 1024,
};

const formRuleSchema = z.object({
  field: z.string(),
  operator: z.string(),
  service: z.enum(['ANY', 'DATA', 'SMS', 'VOICE']).optional(),
  amount: z.number().finite().optional(),
  unit: z.enum(['B', 'KB', 'MB', 'GB']).optional(),
  booleanValue: z.boolean().optional(),
  values: z.array(z.string()).max(50),
});

/** A local fail-closed mirror of API validation for form-friendly values. */
export const filterEditorSchema = (channels: readonly AudienceChannel[]) =>
  z
    .object({ rules: z.array(formRuleSchema).max(50) })
    .superRefine((form, context) => {
      const ranges = new Map<
        CampaignFilterField,
        { minimum?: number; maximum?: number }
      >();
      const active: CampaignFilterFormRule[] = [];
      const inactive: CampaignFilterFormRule[] = [];

      form.rules.forEach((rule, index) => {
        const typedRule = rule as CampaignFilterFormRule;
        const definition = typedRule.field
          ? campaignFilterDefinition(typedRule.field as CampaignFilterField)
          : undefined;
        const path = ['rules', index] as const;
        if (!definition) {
          context.addIssue({
            code: 'custom',
            path: [...path, 'field'],
            message: 'Choose a targeting field.',
          });
          return;
        }
        if (definition.scope === 'APP_USER' && channels.includes('SMS')) {
          context.addIssue({
            code: 'custom',
            path: [...path, 'field'],
            message: `${definition.label} cannot be used with SMS.`,
          });
        }
        if (
          !definition.operators.includes(
            typedRule.operator as CampaignFilterOperator,
          )
        ) {
          context.addIssue({
            code: 'custom',
            path: [...path, 'operator'],
            message: 'Choose a valid operator.',
          });
        }

        if (definition.valueKind === 'NUMBER') {
          const amount = typedRule.amount;
          const multiplier =
            definition.unit === 'BYTES'
              ? byteMultipliers[typedRule.unit ?? 'B']
              : 1;
          const value = amount === undefined ? Number.NaN : amount * multiplier;
          if (
            !Number.isSafeInteger(value) ||
            value < (definition.minimum ?? 0) ||
            value > (definition.maximum ?? Number.MAX_SAFE_INTEGER)
          ) {
            context.addIssue({
              code: 'custom',
              path: [...path, 'amount'],
              message: `Enter a whole number from ${definition.minimum ?? 0} to ${definition.maximum ?? Number.MAX_SAFE_INTEGER}.`,
            });
          }
          if (definition.unit === 'BYTES' && !typedRule.unit)
            context.addIssue({
              code: 'custom',
              path: [...path, 'unit'],
              message: 'Choose a data unit.',
            });
          const range =
            ranges.get(typedRule.field as CampaignFilterField) ?? {};
          if (typedRule.operator === 'GTE')
            range.minimum = Math.max(range.minimum ?? value, value);
          if (typedRule.operator === 'LTE')
            range.maximum = Math.min(range.maximum ?? value, value);
          if (
            range.minimum !== undefined &&
            range.maximum !== undefined &&
            range.minimum > range.maximum
          )
            context.addIssue({
              code: 'custom',
              path: [...path, 'amount'],
              message: `This range conflicts with another ${definition.label} rule.`,
            });
          ranges.set(typedRule.field as CampaignFilterField, range);
        } else if (definition.valueKind === 'RECENCY') {
          if (
            !definition.services?.includes(
              typedRule.service as CampaignFilterService,
            )
          )
            context.addIssue({
              code: 'custom',
              path: [...path, 'service'],
              message: 'Choose an activity service.',
            });
          const allowedRecencyValues =
            definition.values?.map((value) => Number(value)) ?? [];
          if (!allowedRecencyValues.includes(typedRule.amount ?? Number.NaN))
            context.addIssue({
              code: 'custom',
              path: [...path, 'amount'],
              message: `Choose ${definition.values?.join(', ') ?? 'a supported number of'} days.`,
            });
          if (typedRule.field === 'active_within_days') active.push(typedRule);
          if (typedRule.field === 'inactive_for_days') inactive.push(typedRule);
        } else if (definition.valueKind === 'BOOLEAN') {
          if (typeof typedRule.booleanValue !== 'boolean')
            context.addIssue({
              code: 'custom',
              path: [...path, 'booleanValue'],
              message: 'Choose yes or no.',
            });
        } else {
          if (typedRule.values.length === 0)
            context.addIssue({
              code: 'custom',
              path: [...path, 'values'],
              message: 'Select at least one value.',
            });
          typedRule.values.forEach((value) => {
            const normalized = value.trim();
            if (
              !normalized ||
              normalized.length > 128 ||
              containsControlCharacter(normalized) ||
              (definition.valueKind === 'ENUM' &&
                !definition.values?.includes(normalized))
            ) {
              context.addIssue({
                code: 'custom',
                path: [...path, 'values'],
                message: 'Select valid values.',
              });
            }
          });
        }
      });

      for (const activeRule of active)
        for (const inactiveRule of inactive) {
          if (
            (inactiveRule.service === 'ANY' ||
              activeRule.service === inactiveRule.service) &&
            (activeRule.amount ?? 0) <= (inactiveRule.amount ?? 0)
          ) {
            context.addIssue({
              code: 'custom',
              path: ['rules'],
              message: 'Active and inactive rules conflict.',
            });
          }
        }
    });

export function toApiFilter(
  rules: readonly CampaignFilterFormRule[],
): CampaignFilterInput {
  const normalized = rules
    .map(toApiRule)
    .sort((left, right) =>
      canonicalRule(left).localeCompare(canonicalRule(right)),
    );
  const unique = new Map<string, CampaignFilterRule>();
  for (const rule of normalized) unique.set(canonicalRule(rule), rule);
  return { version: 1, logic: 'AND', rules: [...unique.values()] };
}

function toApiRule(rule: CampaignFilterFormRule): CampaignFilterRule {
  const definition = campaignFilterDefinition(
    rule.field as CampaignFilterField,
  );
  if (!definition)
    throw new Error('Cannot convert an unknown targeting field.');
  const base = {
    field: rule.field as CampaignFilterField,
    operator: rule.operator as CampaignFilterOperator,
  };
  if (definition.valueKind === 'RECENCY')
    return {
      ...base,
      service: rule.service,
      value: rule.amount,
    } as CampaignFilterRule;
  if (definition.valueKind === 'NUMBER')
    return {
      ...base,
      value:
        (rule.amount ?? 0) *
        (definition.unit === 'BYTES' ? byteMultipliers[rule.unit ?? 'B'] : 1),
    };
  if (definition.valueKind === 'BOOLEAN')
    return { ...base, value: rule.booleanValue } as CampaignFilterRule;
  return {
    ...base,
    values: [...new Set(rule.values.map((value) => value.trim()))].sort(),
  };
}

function canonicalRule(rule: CampaignFilterRule): string {
  return JSON.stringify({
    field: rule.field,
    operator: rule.operator,
    ...(rule.service === undefined ? {} : { service: rule.service }),
    ...(rule.value === undefined ? {} : { value: rule.value }),
    ...(rule.values === undefined ? {} : { values: rule.values }),
  });
}

export function campaignFilterSummary(filter: CampaignFilterInput): string {
  if (filter.rules.length === 0) return 'No audience filters';
  const ordered = [...filter.rules].sort(
    (left, right) =>
      summaryRank(left) - summaryRank(right) ||
      canonicalRule(left).localeCompare(canonicalRule(right)),
  );
  const hasRecencyRule = ordered.some(
    (rule) =>
      rule.field === 'active_within_days' || rule.field === 'inactive_for_days',
  );
  return ordered.map(summaryForRule).join(hasRecencyRule ? ', ' : ' AND ');
}

function summaryForRule(rule: CampaignFilterRule): string {
  const definition = campaignFilterDefinition(rule.field);
  if (!definition) return rule.field;
  if (rule.field === 'active_within_days')
    return `Active ${String(rule.service).toLowerCase()} users in the last ${rule.value} days`;
  if (rule.field === 'inactive_for_days')
    return `Inactive ${String(rule.service).toLowerCase()} users for ${rule.value} days`;
  if (definition.valueKind === 'NUMBER') {
    const quantity =
      definition.unit === 'BYTES'
        ? formatBytes(rule.value as number)
        : String(rule.value);
    const operator = rule.operator === 'GTE' ? 'at least' : 'at most';
    if (definition.unit === 'BYTES') return `${operator} ${quantity}`;
    if (rule.field === 'active_days_30d')
      return `${operator} ${quantity} active days`;
    return `${definition.label}, ${operator} ${quantity}`;
  }
  if (rule.values) return `${definition.label} is ${rule.values.join(' OR ')}`;
  return `${definition.label} is ${rule.value ? 'yes' : 'no'}`;
}

function summaryRank(rule: CampaignFilterRule): number {
  if (rule.field === 'active_within_days' || rule.field === 'inactive_for_days')
    return 0;
  if (rule.field.startsWith('data_bytes_')) return 1;
  if (rule.field === 'active_days_30d') return 2;
  return 3;
}

function formatBytes(bytes: number): string {
  for (const [unit, size] of [
    ['GB', 1024 ** 3],
    ['MB', 1024 ** 2],
    ['KB', 1024],
    ['B', 1],
  ] as const)
    if (bytes % size === 0) return `${bytes / size} ${unit}`;
  return `${bytes} B`;
}

function containsControlCharacter(value: string): boolean {
  return [...value].some((character) => {
    const code = character.charCodeAt(0);
    return code <= 31 || code === 127;
  });
}
